import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { usersApi, ebosApi, rbacApi } from '../api';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import BulkImportUsersModal from '../components/BulkImportUsersModal';
import { Plus, X, Pencil, Loader2, Send, ShieldCheck, Info, Save, Trash2, RotateCcw, ChevronDown, ChevronUp, Upload } from 'lucide-react';

const TabButton = ({ active, onClick, children, primary }) => (
  <button onClick={onClick} className={`px-4 py-1.5 rounded-md text-[13px] font-medium transition-colors ${active ? 'text-white' : 'text-[#232323] hover:bg-[#F1EBDD]'}`}
    style={active ? { background: primary } : {}}>{children}</button>
);

const ROLE_INFO = {
  SuperAdmin: { label: 'Super Admin', tone: 'wine', chip: 'Root' },
  Admin: { label: 'Admin', tone: 'wine' },
  AllIndiaManager: { label: 'All-India Manager', tone: 'blue' },
  RetailManager: { label: 'Retail Manager', tone: 'blue' },
  ZoneManager: { label: 'Zone Manager', tone: 'blue' },
  EBO: { label: 'EBO', tone: 'green' },
  Warehouse: { label: 'Warehouse', tone: 'amber' },
  Logistics: { label: 'Logistics', tone: 'blue' },
};

const ZONES = ['North', 'South', 'East', 'West'];

export default function UsersRoles() {
  const { theme, user: currentUser } = useAuth();
  const isSuper = currentUser?.role === 'SuperAdmin';
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [ebos, setEbos] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [showDeletedPanel, setShowDeletedPanel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ name: '', email: '', role: 'EBO', scope: '', branchCode: '', zone: '' });
  const [toast, setToast] = useState('');

  const load = () => {
    setLoading(true);
    const promises = [usersApi.list(), ebosApi.list()];
    if (isSuper) promises.push(usersApi.listDeleted());
    Promise.all(promises).then(([u, e, d]) => {
      setUsers(u);
      setEbos(e);
      if (isSuper) setDeleted(d || []);
    }).finally(() => setLoading(false));
  };
  useEffect(load, [isSuper]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3200); };

  const openAdd = () => {
    setEditing(null);
    setDraft({ name: '', email: '', role: 'EBO', scope: '', branchCode: '', zone: '' });
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditing(u.id);
    setDraft({ name: u.name, email: u.email, role: u.role, scope: u.scope || '', branchCode: u.branchCode || '', zone: u.zone || '' });
    setShowModal(true);
  };

  const save = async () => {
    if (!draft.name || !draft.email) return;
    // Enforce corporate-domain rule on the client — mirrors backend guard
    if (!editing) {
      const email = draft.email.trim().toLowerCase();
      const domain = email.split('@')[1];
      if (!domain || (domain !== 'futuremakeup.in' && domain !== 'futuremakeup.com')) {
        alert('Email must end with @futuremakeup.in or @futuremakeup.com. External domains are not permitted.');
        return;
      }
    }
    try {
      const payload = { ...draft, email: draft.email.trim().toLowerCase() };
      if (payload.role === 'EBO') {
        const ebo = ebos.find(e => e.code === payload.branchCode);
        payload.scope = ebo?.name || 'FutureMakeup EBO';
        payload.zone = null;
      } else if (payload.role === 'ZoneManager') {
        payload.branchCode = null;
        payload.scope = payload.scope || `${payload.zone} zone`;
      } else {
        payload.branchCode = null; payload.zone = null;
        payload.scope = payload.scope || 'All branches';
      }
      if (editing) {
        await usersApi.update(editing, { name: payload.name, role: payload.role, scope: payload.scope, branchCode: payload.branchCode, zone: payload.zone });
        showToast('User updated');
      } else {
        const r = await usersApi.add(payload);
        if (r.inviteSent === false && r.inviteError) {
          showToast(`User added — but invite email failed: ${r.inviteError}`);
        } else if (r.inviteSent) {
          showToast(`User added · invite emailed to ${payload.email}`);
        } else {
          showToast('User added');
        }
      }
      setShowModal(false); setEditing(null); load();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (id, email) => {
    if (id === currentUser.id) return alert('You cannot remove your own account');
    if (email === 'rajat@futuremakeup.com') return alert('The root Super Admin cannot be removed');
    if (!window.confirm(`Remove user ${email}? This cannot be undone.`)) return;
    try { await usersApi.remove(id); showToast('User removed'); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Remove failed'); }
  };

  const sendInvite = async (id, email) => {
    if (!window.confirm(`Send a fresh sign-in invite email to ${email}?`)) return;
    try { const r = await usersApi.sendInvite(id); showToast(`Invite emailed to ${r.sent_to}`); }
    catch (e) { alert(e.response?.data?.detail || 'Send failed'); }
  };

  const restore = async (email) => {
    if (!window.confirm(`Restore ${email}? The account will be re-created with its previous role and a fresh invite email will be sent.`)) return;
    try {
      const r = await usersApi.restore(email);
      if (r.inviteSent) showToast(`Restored · invite emailed to ${email}`);
      else if (r.restored) showToast(`Restored ${email} (invite send failed: ${r.inviteError || 'unknown'})`);
      else showToast(`Tombstone cleared for ${email}`);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Restore failed'); }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
  };

  const roleBadge = (role) => {
    const map = {
      SuperAdmin: { bg: '#F7E7EA', color: '#7A2E41' },
      Admin: { bg: '#F7E7EA', color: '#9C3D54' },
      AllIndiaManager: { bg: '#E3EEF7', color: '#265f8a' },
      RetailManager: { bg: '#E3EEF7', color: '#265f8a' },
      ZoneManager: { bg: '#E3EEF7', color: '#265f8a' },
      EBO: { bg: '#E1EEE4', color: '#2f6b41' },
      Warehouse: { bg: '#FBEED8', color: '#8A5A1E' },
      Logistics: { bg: '#E3EEF7', color: '#265f8a' },
    };
    const s = map[role] || { bg: '#EEE', color: '#333' };
    return <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-medium" style={{ background: s.bg, color: s.color }}>{role === 'SuperAdmin' && <ShieldCheck size={11} />}{ROLE_INFO[role]?.label || role}</span>;
  };

  return (
    <div>
      <PageHeader
        title="Users & roles"
        subtitle={isSuper
          ? 'You are signed in as Super Admin. Only your role can add / remove users and edit the permissions matrix.'
          : 'You have view-only access to the directory. Only the Super Admin can add / remove users or change the permissions matrix.'
        }
      />

      {!isSuper && (
        <div className="fm-card p-4 mb-5 flex items-start gap-3 border-l-4" style={{ borderLeftColor: '#B78140' }}>
          <Info size={16} className="mt-0.5 text-[#B78140] shrink-0" />
          <div className="text-[13px] text-[#232323] leading-relaxed">
            <b>Read-only view.</b> To add / remove / change roles of any user, or to update the permissions matrix, contact the Super Admin at <span className="font-mono">rajat@futuremakeup.com</span>.
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-5">
        <TabButton active={tab==='users'} onClick={() => setTab('users')} primary={theme.primary}>Users</TabButton>
        <TabButton active={tab==='matrix'} onClick={() => setTab('matrix')} primary={theme.primary}>Permissions matrix</TabButton>
      </div>

      {toast && <div className="mb-3 text-[13px] px-3 py-2 rounded bg-[#E1EEE4] text-[#2f6b41] inline-block">{toast}</div>}

      {tab === 'users' && (
        <>
          <UsersTab
            isSuper={isSuper} users={users} loading={loading} theme={theme}
            currentUser={currentUser} openAdd={openAdd} openEdit={openEdit}
            openBulkImport={() => setShowBulkImport(true)}
            sendInvite={sendInvite} remove={remove} roleBadge={roleBadge}
            showModal={showModal} setShowModal={setShowModal}
            draft={draft} setDraft={setDraft} save={save} editing={editing}
            ebos={ebos}
          />
          <BulkImportUsersModal
            open={showBulkImport}
            onClose={() => setShowBulkImport(false)}
            onDone={() => { load(); setToast('Bulk import complete'); setTimeout(() => setToast(''), 3500); }}
            theme={theme}
          />
          {isSuper && (
            <div className="fm-card mt-6" data-testid="deleted-users-panel">
              <button
                onClick={() => setShowDeletedPanel(v => !v)}
                className="w-full flex items-center justify-between px-5 py-3 text-left"
                data-testid="deleted-users-toggle"
              >
                <div className="flex items-center gap-2 text-[13.5px]">
                  <Trash2 size={14} className="text-[#7a6f5c]" />
                  <span className="font-medium">Recently deleted users</span>
                  <span className="text-[11.5px] text-[#7a6f5c]">({deleted.length})</span>
                </div>
                {showDeletedPanel ? <ChevronUp size={14} className="text-[#7a6f5c]" /> : <ChevronDown size={14} className="text-[#7a6f5c]" />}
              </button>
              {showDeletedPanel && (
                <div className="border-t border-[#E7E2D3] fm-scroll-x">
                  {deleted.length === 0 ? (
                    <div className="px-5 py-8 text-center text-[13px] text-[#7a6f5c]">No deleted users. Anything you remove from the table above will appear here for one-click restore.</div>
                  ) : (
                    <table className="w-full text-[13.5px]" data-testid="deleted-users-table">
                      <thead>
                        <tr className="text-left text-[12px] text-[#7a6f5c] border-b border-[#E7E2D3] whitespace-nowrap">
                          <th className="px-4 py-3 font-medium">Email</th>
                          <th className="px-4 py-3 font-medium">Name</th>
                          <th className="px-4 py-3 font-medium">Role</th>
                          <th className="px-4 py-3 font-medium">Deleted at</th>
                          <th className="px-4 py-3 font-medium">Deleted by</th>
                          <th className="px-4 py-3 font-medium text-right pr-6">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deleted.map(d => (
                          <tr key={d.email} className="fm-row border-t border-[#EFEADC]">
                            <td className="px-4 py-3">{d.email}</td>
                            <td className="px-4 py-3">{d.lastName || '—'}</td>
                            <td className="px-4 py-3">{d.lastRole ? roleBadge(d.lastRole) : '—'}</td>
                            <td className="px-4 py-3 text-[12.5px] text-[#7a6f5c]">{formatDate(d.deletedAt)}</td>
                            <td className="px-4 py-3 text-[12.5px] text-[#7a6f5c]">{d.deletedBy || '—'}</td>
                            <td className="px-4 py-3 text-right pr-6">
                              <button
                                data-testid={`restore-user-${d.email}`}
                                onClick={() => restore(d.email)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E7E2D3] bg-white text-[12px] hover:bg-[#F7F4EA]"
                              >
                                <RotateCcw size={12} /> Restore
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tab === 'matrix' && <MatrixTab isSuper={isSuper} theme={theme} />}
    </div>
  );
}

function UsersTab({ isSuper, users, loading, theme, currentUser, openAdd, openEdit, openBulkImport, sendInvite, remove, roleBadge, showModal, setShowModal, draft, setDraft, save, editing, ebos }) {
  // iter37 — search + role filter (kept local to the tab so state doesn't
  // leak into the matrix/tombstone tabs).
  const [userQuery, setUserQuery] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const filteredUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    return users.filter(u => {
      if (userRoleFilter && u.role !== userRoleFilter) return false;
      if (!q) return true;
      return (u.name || '').toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q)
        || (u.role || '').toLowerCase().includes(q);
    });
  }, [users, userQuery, userRoleFilter]);
  return (
    <>
      {isSuper && (
        <div className="flex justify-end gap-2 mb-3">
          <button onClick={openBulkImport} data-testid="users-bulk-import-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]">
            <Upload size={13}/> Bulk import
          </button>
          <button onClick={openAdd} data-testid="users-add-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-white text-[13px]" style={{ background: theme.primary }}>
            <Plus size={13}/> Add user
          </button>
        </div>
      )}

      {/* iter37 — search + role filter */}
      <div className="fm-card p-3 mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <input
            data-testid="users-search"
            type="text"
            value={userQuery}
            onChange={(e) => setUserQuery(e.target.value)}
            placeholder="Search by name or email…"
            className="fm-focus w-full border border-[#E7E2D3] rounded-md pl-3 pr-3 py-2 text-[13.5px] bg-white"
          />
        </div>
        <select
          data-testid="users-role-filter"
          value={userRoleFilter}
          onChange={(e) => setUserRoleFilter(e.target.value)}
          className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white min-w-[160px]"
        >
          <option value="">All roles</option>
          <option value="SuperAdmin">Super Admin</option>
          <option value="Admin">Admin</option>
          <option value="AllIndiaManager">All-India Manager</option>
          <option value="ZoneManager">Zone Manager</option>
          <option value="RetailManager">Retail Manager</option>
          <option value="EBO">EBO</option>
          <option value="Warehouse">Warehouse</option>
          <option value="Logistics">Logistics</option>
        </select>
        <div className="text-[12px] text-[#7a6f5c]">
          {filteredUsers.length} of {users.length} shown
        </div>
      </div>

      <div className="fm-card overflow-hidden">
        {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div> : (
          <table className="w-full text-[14px]">
            <thead>
              <tr className="text-left text-[12px] text-[#7a6f5c] border-b border-[#E7E2D3]">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Role</th>
                <th className="px-5 py-3 font-medium">Scope</th>
                <th className="px-5 py-3 font-medium">Status</th>
                {isSuper && <th className="px-5 py-3 font-medium text-right pr-6">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(u => {
                const isRoot = u.email === 'rajat@futuremakeup.com';
                return (
                  <tr key={u.id} className="fm-row border-t border-[#EFEADC]">
                    <td className="px-5 py-3 font-medium">
                      {u.name}
                      {isRoot && <span className="ml-2 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider text-[#7A2E41]"><ShieldCheck size={11}/> Root</span>}
                    </td>
                    <td className="px-5 py-3 text-[13px]">{u.email}</td>
                    <td className="px-5 py-3">{roleBadge(u.role)}</td>
                    <td className="px-5 py-3 text-[13px]">{u.scope}{u.zone ? ` · ${u.zone}` : ''}</td>
                    <td className="px-5 py-3"><StatusBadge status={u.status || 'Active'} small /></td>
                    {isSuper && (
                      <td className="px-5 py-3 text-right pr-6">
                        <div className="inline-flex items-center gap-3">
                          <button onClick={() => sendInvite(u.id, u.email)} className="inline-flex items-center gap-1 text-[13px] text-[#7a6f5c] hover:text-[#232323]" title="Send fresh sign-in invite email"><Send size={12}/> Send invite</button>
                          <button onClick={() => openEdit(u)} className="inline-flex items-center gap-1 text-[13px] text-[#7a6f5c] hover:text-[#232323]"><Pencil size={12}/> Edit</button>
                          {!isRoot && u.id !== currentUser.id && (
                            <button onClick={() => remove(u.id, u.email)} className="inline-flex items-center gap-1 text-[13px] text-[#7a6f5c] hover:text-[#8a2f2f]"><X size={12}/> Remove</button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-[12px] text-[#7a6f5c] mt-3">
        OTP-only sign-in — no passwords stored. "Send invite" emails the user their access details so they can request a fresh OTP.
      </p>

      {showModal && isSuper && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="fm-card p-5 w-full max-w-[500px]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold text-[16px]">{editing ? 'Edit user' : 'Add user'}</div>
              <button onClick={() => setShowModal(false)}><X size={16}/></button>
            </div>
            <div className="space-y-3">
              <F label="Full name" value={draft.name} onChange={v => setDraft({ ...draft, name: v })} />
              <F label="Email (@futuremakeup.in / .com)" value={draft.email} onChange={v => setDraft({ ...draft, email: v })} disabled={!!editing} />
              <div>
                <label className="text-[12.5px] font-medium">Role</label>
                <select value={draft.role} onChange={e => setDraft({ ...draft, role: e.target.value })} className="fm-select fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white">
                  <option value="SuperAdmin">Super Admin</option>
                  <option value="Admin">Admin</option>
                  <option value="AllIndiaManager">All-India Manager</option>
                  <option value="RetailManager">Retail Manager</option>
                  <option value="ZoneManager">Zone Manager</option>
                  <option value="EBO">EBO</option>
                  <option value="Warehouse">Warehouse</option>
                  <option value="Logistics">Logistics</option>
                </select>
              </div>
              {draft.role === 'EBO' && (
                <div>
                  <label className="text-[12.5px] font-medium">EBO Branch</label>
                  <select value={draft.branchCode} onChange={e => setDraft({ ...draft, branchCode: e.target.value })} className="fm-select fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white">
                    <option value="">Select branch</option>
                    {ebos.map(e => <option key={e.code} value={e.code}>{e.name} ({e.code})</option>)}
                  </select>
                </div>
              )}
              {draft.role === 'ZoneManager' && (
                <div>
                  <label className="text-[12.5px] font-medium">Zone</label>
                  <select value={draft.zone} onChange={e => setDraft({ ...draft, zone: e.target.value })} className="fm-select fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white">
                    <option value="">Select zone</option>
                    {ZONES.map(z => <option key={z} value={z}>{z}</option>)}
                  </select>
                </div>
              )}
              {(draft.role !== 'EBO' && draft.role !== 'ZoneManager') && (
                <F label="Scope description" value={draft.scope} onChange={v => setDraft({ ...draft, scope: v })} />
              )}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowModal(false)} className="px-3 py-2 border border-[#E7E2D3] rounded-md text-[13.5px] hover:bg-[#F7F4EA]">Cancel</button>
              <button onClick={save} className="px-4 py-2 rounded-md text-white text-[13.5px] font-medium" style={{ background: theme.primary }}>{editing ? 'Save changes' : 'Add user'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MatrixTab({ isSuper, theme }) {
  const [perms, setPerms] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    Promise.all([rbacApi.permissions(), rbacApi.getMatrix()]).then(([p, m]) => {
      setPerms(p.permissions);
      setRoles(p.roles);
      setMatrix(m.matrix);
      setDefaults(m.defaults);
    }).catch(() => {});
  }, []);

  if (!perms || !matrix) return <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div>;

  const toggle = (role, key) => {
    if (!isSuper) return;
    setMatrix(m => {
      const next = { ...m, [role]: [...(m[role] || [])] };
      if (next[role].includes(key)) next[role] = next[role].filter(k => k !== key);
      else next[role] = [...next[role], key];
      return next;
    });
  };

  const resetDefaults = () => {
    if (!isSuper) return;
    if (!window.confirm('Reset the permissions matrix to shipping defaults?')) return;
    setMatrix(JSON.parse(JSON.stringify(defaults)));
  };

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await rbacApi.setMatrix(matrix);
      setMsg('Permissions matrix saved');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <p className="text-[13px] text-[#7a6f5c] mb-3 leading-relaxed max-w-[1000px]">
        Tick the permissions each role should have. Super Admin is not listed — it always has every permission. <span className="font-medium text-[#232323]">User management</span> (add / remove users, edit this matrix) is hard-locked to Super Admin and cannot be granted to any other role.
      </p>

      <div className="fm-card overflow-x-auto">
        <table className="w-full text-[13.5px]">
          <thead>
            <tr className="text-left border-b border-[#E7E2D3]">
              <th className="px-5 py-3 text-[12px] text-[#7a6f5c] font-medium sticky left-0 bg-[#FBFAF7] min-w-[320px]">Permission</th>
              {roles.map(r => (
                <th key={r} className="px-3 py-3 text-[12px] text-[#7a6f5c] font-medium text-center whitespace-nowrap">
                  {ROLE_INFO[r]?.label || r}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {perms.map(p => (
              <tr key={p.key} className="border-t border-[#EFEADC]">
                <td className="px-5 py-3 sticky left-0 bg-[#FBFAF7]">
                  <div className="font-medium text-[13.5px]">{p.label}</div>
                  <div className="text-[11px] text-[#7a6f5c] font-mono">{p.key}</div>
                </td>
                {roles.map(r => (
                  <td key={r} className="px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-[#9C3D54] cursor-pointer disabled:cursor-not-allowed"
                      checked={(matrix[r] || []).includes(p.key)}
                      onChange={() => toggle(r, p.key)}
                      disabled={!isSuper}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isSuper && (
        <div className="flex items-center gap-3 mt-5">
          <button disabled={saving} onClick={save} className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-white text-[14px] font-medium disabled:opacity-60" style={{ background: theme.primary }}>
            <Save size={14} /> {saving ? 'Saving…' : 'Save permissions matrix'}
          </button>
          <button onClick={resetDefaults} className="px-3 py-2 rounded-md border border-[#E7E2D3] text-[13.5px] hover:bg-[#F7F4EA]">
            Reset to defaults
          </button>
          {msg && <span className="text-[13px] text-[#2f6b41]">{msg}</span>}
        </div>
      )}
    </div>
  );
}

function F({ label, value, onChange, disabled }) {
  return (
    <div>
      <label className="text-[12.5px] font-medium">{label}</label>
      <input value={value} disabled={disabled} onChange={e => onChange(e.target.value)} className={`fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] ${disabled ? 'bg-[#F7F4EA] text-[#7a6f5c]' : 'bg-white'}`} />
    </div>
  );
}
